import React, {useState, useEffect, useRef} from "react";
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Alert,
  Stack,
} from "@mui/material";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useSendEmail,
  useUploadReport,
  validationSchema,
} from "../reports/Services";
import { EmailForm } from "./types";
import { jasperPrevBlobPrev } from "./Services";
import {useGeneraAnalisiRequisiti} from "../gestprev/Services";

interface ReportPrevProps {
  onClose: () => void;
  idprev: number;
  formato: string;
}

interface FormValues {
  emailTo?: string;
}

const ReportPrev: React.FC<ReportPrevProps> = ({ onClose, idprev, formato }) => {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [emailTo, setEmailTo] = useState("");

  const {
    register,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const { mutateAsync: uploadReport, isPending: isUploading } = useUploadReport();
  const {
    mutateAsync: sendEmail,
    isPending: isSendingEmail,
    isSuccess: isSuccessSendEmail,
  } = useSendEmail();

  const { mutateAsync: analisiRequisiti, data: analisiRequisitiData, error: analisiRequisitiError, isPending: analisiRequisitiLoading } = useGeneraAnalisiRequisiti();

   const isDownloadingRef = useRef(false);

    // useEffect(() => {
    //   const fetchBlob = async () => {
    //     try {
    //       const myBlob = await jasperPrevBlobPrev(idprev, formato);
    //       setBlob(myBlob || null);
    //     } catch (error) {
    //       console.error("Error fetching blob:", error);
    //     }
    //   };
    //   fetchBlob();
    // }, [idprev, formato]);
    //
useEffect(() => {
  if (formato !== "pdf") return;

  const fetchBlob = async () => {
    try {
      const myBlob = await jasperPrevBlobPrev(idprev, formato);
      setBlob(myBlob || null);
    } catch (error) {
      console.error("Error fetching blob:", error);
    }
  };

  fetchBlob();
}, [idprev, formato]);



const handleDownload = async () => {
  if (isDownloadingRef.current) return; // ⛔ blocca doppio trigger
  isDownloadingRef.current = true;

  try {
    // 🔵 PDF
    if (formato === "pdf") {
      if (!blob) return;

      const fileName = `Preventivo_${idprev}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    }

    // 🟢 XLSX
    if (formato === "xlsx") {
      const xlsBlob = await jasperPrevBlobPrev(idprev, "xlsx");
      if (!xlsBlob) return;

      const fileName = `Preventivo_${idprev}.xlsx`;
      const url = window.URL.createObjectURL(xlsBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    }

    // 🔴 Analisi Requisiti (DOC)
    if (formato === "anareq") {
      const docBlob = await analisiRequisiti(idprev);
      if (!docBlob) return;

      // const fileName = `AnalisiRequisiti_${idprev}.docx`;
      // const url = window.URL.createObjectURL(docBlob);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = fileName;
      // a.click();
      // window.URL.revokeObjectURL(url);
    }
  } finally {
    // riabilita dopo un micro-delay
    setTimeout(() => {
      isDownloadingRef.current = false;
    }, 300);
  }
};


  const handleEmail = async () => {
  if (!emailTo) return;

  // 🔵 PDF
  if (formato === "pdf") {
    if (!blob) return;

    const fileName = `Preventivo_${idprev}.pdf`;
    const formData = new FormData();
    formData.append("file", blob, fileName);

    await uploadReport(formData);

    await sendEmail({
      email: emailTo,
      subject: "Report Preventivo",
      attachment: fileName,
      tipo: "report",
    });
  }

  // 🔴 Analisi Requisiti (DOC)
  if (formato === "anareq") {
    const docBlob = await analisiRequisiti(idprev);
    if (!docBlob) return;

    const fileName = `AnalisiRequisiti_${idprev}.docx`;
    const formData = new FormData();
    formData.append("file", docBlob, fileName);

    await uploadReport(formData);

    await sendEmail({
      email: emailTo,
      subject: "Analisi Requisiti",
      attachment: fileName,
      tipo: "anareq",
    });
  }
};



  return (
    <Dialog onClose={onClose} open fullWidth maxWidth="sm">
      <DialogContent>
        <Stack spacing={2}>
          {/* Email input */}
          <TextField
            id="emailTo"
            label="Email per invio Report"
            type="email"
            fullWidth
            value={emailTo}
            {...register("emailTo")}
            onChange={(e) => setEmailTo(e.target.value)}
            error={!!errors.emailTo}
            helperText={errors.emailTo?.message}
          />

          {/* Buttons row */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="error" onClick={onClose}>
              Chiudi
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownload}
              disabled={
                (formato === "pdf" && !blob)
              }
            >
              Download {formato.toUpperCase()}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmail}
              disabled={!emailTo || isUploading || isSendingEmail}
            >
              Invia Email
            </Button>
          </Stack>

          {/* Success alert */}
          {isSuccessSendEmail && (
            <Alert variant="filled" severity="success">
              Invio email avvenuto correttamente a {emailTo}!
            </Alert>
          )}

          {/* Preview */}
          {formato === "pdf" && blob && (
            <iframe
              src={window.URL.createObjectURL(blob)}
              width="100%"
              height="800"
              title="Anteprima PDF"
              style={{ border: "none" }}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPrev;
